import { supabase } from './supabase';

/**
 * Tag interface
 */
export interface Tag {
  id: number;
  name: string;
  description: string | null;
  color: string;
  project_id: number;
  category_id: number | null;
  created_at: string;
  updated_at: string;
  usage_count?: number;
  category?: {
    id: number;
    name: string;
    color: string;
  };
}

/**
 * Get all tags for a project
 * @param projectId - Project ID
 * @returns Promise with tags array
 */
export const getProjectTags = async (projectId: string | number): Promise<Tag[]> => {
  try {
    const parsedProjectId = typeof projectId === 'string' ? parseInt(projectId) : projectId;

    // Direct query to get tags with usage count and category info
    const { data, error } = await supabase
      .from('tags')
      .select(`
        id,
        name,
        description,
        color,
        project_id,
        category_id,
        created_at,
        updated_at,
        plotcard_tags(count),
        tag_categories (
          id,
          name,
          color
        )
      `)
      .eq('project_id', parsedProjectId)
      .order('name');

    if (error) {
      console.error('Error fetching project tags:', error);
      throw error;
    }

    // Transform the data to include usage_count and category
    const transformedData = data.map(tag => ({
      ...tag,
      usage_count: tag.plotcard_tags[0]?.count || 0,
      category: tag.tag_categories,
      plotcard_tags: undefined, // Remove the nested array
      tag_categories: undefined // Remove the nested object
    }));

    return transformedData || [];
  } catch (error) {
    console.error('Error in getProjectTags:', error);
    throw error;
  }
};

/**
 * Get tags for a plotcard
 * @param plotcardId - Plotcard ID
 * @returns Promise with tags array
 */
export const getPlotcardTags = async (plotcardId: number): Promise<Tag[]> => {
  try {
    // Direct query to get tags for a plotcard
    const { data, error } = await supabase
      .from('plotcard_tags')
      .select(`
        tag_id,
        tags (
          id,
          name,
          description,
          color
        )
      `)
      .eq('plotcard_id', plotcardId);

    if (error) {
      console.error('Error fetching plotcard tags:', error);
      throw error;
    }

    // Transform the data to get just the tags
    const transformedData = data.map(item => item.tags);

    return transformedData || [];
  } catch (error) {
    console.error('Error in getPlotcardTags:', error);
    throw error;
  }
};

/**
 * Get tags for multiple plotcards in a single query
 * @param plotcardIds - Array of plotcard IDs
 * @returns Promise with a map of plotcard IDs to their tags
 */
export const getMultiplePlotcardTags = async (plotcardIds: number[]): Promise<Record<number, Tag[]>> => {
  try {
    if (!plotcardIds.length) {
      return {};
    }

    // Create a result object with empty arrays for all plotcard IDs
    const result: Record<number, Tag[]> = {};
    plotcardIds.forEach(id => {
      result[id] = [];
    });

    // Check if we have any plotcard IDs to query
    if (plotcardIds.length === 0) {
      console.log('No plotcard IDs to fetch tags for');
      return result;
    }

    // Log the query we're about to make
    console.log(`Fetching tags for ${plotcardIds.length} plotcards: ${plotcardIds.join(', ')}`);

    // Direct query to get tags for multiple plotcards
    const { data, error } = await supabase
      .from('plotcard_tags')
      .select(`
        plotcard_id,
        tag_id,
        tags (
          id,
          name,
          description,
          color
        )
      `)
      .in('plotcard_id', plotcardIds);

    if (error) {
      console.error('Error fetching multiple plotcard tags:', error);
      throw error;
    }

    // Log the response
    console.log(`Received ${data.length} tag associations for ${plotcardIds.length} plotcards`);

    // If we got no data, return the result with empty arrays
    if (!data || data.length === 0) {
      console.log('No tags found for any of the plotcards');
      return result;
    }

    // Populate with actual data
    data.forEach(item => {
      const plotcardId = item.plotcard_id;
      if (!result[plotcardId]) {
        result[plotcardId] = [];
      }
      if (item.tags) {
        // Log each tag being added
        console.log(`Adding tag to plotcard ${plotcardId}:`, item.tags);
        result[plotcardId].push(item.tags);
      }
    });

    // Log the final result
    console.log('Final tags map:', result);

    return result;
  } catch (error) {
    console.error('Error in getMultiplePlotcardTags:', error);
    throw error;
  }
};

/**
 * Prefetch and cache tags for all plotcards in a timeline
 * This function is meant to be called during the initial page load
 * to prefetch all tags for all plotcards in a timeline
 *
 * @param queryClient - The TanStack QueryClient instance
 * @param plotcardIds - Array of plotcard IDs to prefetch tags for
 * @returns Promise that resolves when all tags are prefetched and cached
 */
export const prefetchAndCachePlotcardTags = async (
  queryClient: any,
  plotcardIds: number[]
): Promise<void> => {
  try {
    if (!plotcardIds.length) {
      console.log('No plotcard IDs provided for prefetching tags');
      return;
    }

    // Batch the plotcardIds into chunks of 50 to avoid URL length limits
    const batchSize = 50;
    const batches = [];
    for (let i = 0; i < plotcardIds.length; i += batchSize) {
      batches.push(plotcardIds.slice(i, i + batchSize));
    }

    console.log(`Prefetching tags for ${plotcardIds.length} plotcards in ${batches.length} batches`);

    // Process each batch
    for (let i = 0; i < batches.length; i++) {
      const batchIds = batches[i];
      console.log(`Processing batch ${i + 1}/${batches.length} with ${batchIds.length} plotcards`);

      // Fetch all tags for this batch of plotcards in a single query
      const tagsMap = await getMultiplePlotcardTags(batchIds);

      // Cache the result for the batch query
      const batchQueryKey = ['tags', 'plotcards', batchIds];
      console.log(`Caching batch result with key:`, batchQueryKey);
      queryClient.setQueryData(batchQueryKey, tagsMap);

      // Also cache individual plotcard tag results
      Object.entries(tagsMap).forEach(([plotcardIdStr, tags]) => {
        const plotcardId = parseInt(plotcardIdStr);
        const individualQueryKey = ['tags', 'plotcard', plotcardId];
        console.log(`Caching individual result for plotcard ${plotcardId} with key:`, individualQueryKey);
        queryClient.setQueryData(individualQueryKey, tags);
      });
    }

    console.log(`Successfully prefetched and cached tags for ${plotcardIds.length} plotcards`);
  } catch (error) {
    console.error('Error in prefetchAndCachePlotcardTags:', error);
    // Don't throw the error, just log it - we don't want to break the main timeline loading
  }
};

/**
 * Create a new tag
 * @param tag - Tag data
 * @returns Promise with created tag
 */
export const createTag = async (
  tag: {
    name: string;
    description?: string;
    color?: string;
    project_id: number;
    category_id?: number | null;
  }
): Promise<Tag> => {
  try {
    // Insert the tag - tenant_id will be set by the database trigger
    const { data, error } = await supabase
      .from('tags')
      .insert([
        {
          name: tag.name,
          description: tag.description || null,
          color: tag.color || '#94a3b8',
          project_id: tag.project_id,
          category_id: tag.category_id || null
        }
      ])
      .select()
      .single();

    if (error) {
      console.error('Error creating tag:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in createTag:', error);
    throw error;
  }
};

/**
 * Update a tag
 * @param tagId - Tag ID
 * @param updates - Tag updates
 * @returns Promise with updated tag
 */
export const updateTag = async (
  tagId: number,
  updates: {
    name?: string;
    description?: string | null;
    color?: string;
    category_id?: number | null;
  }
): Promise<Tag> => {
  try {
    const { data, error } = await supabase
      .from('tags')
      .update(updates)
      .eq('id', tagId)
      .select()
      .single();

    if (error) {
      console.error('Error updating tag:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in updateTag:', error);
    throw error;
  }
};

/**
 * Delete a tag
 * @param tagId - Tag ID
 * @returns Promise with success status
 */
export const deleteTag = async (tagId: number): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('tags')
      .delete()
      .eq('id', tagId);

    if (error) {
      console.error('Error deleting tag:', error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteTag:', error);
    throw error;
  }
};

/**
 * Add a tag to a plotcard
 * @param plotcardId - Plotcard ID
 * @param tagId - Tag ID
 * @returns Promise with success status
 */
export const addTagToPlotcard = async (plotcardId: number, tagId: number): Promise<boolean> => {
  try {
    // First check if the relationship already exists
    const { data: existingData, error: existingError } = await supabase
      .from('plotcard_tags')
      .select('id')
      .eq('plotcard_id', plotcardId)
      .eq('tag_id', tagId)
      .maybeSingle();

    if (existingError) {
      console.error('Error checking existing tag relationship:', existingError);
      throw existingError;
    }

    // If the relationship already exists, return true
    if (existingData) {
      return true;
    }

    // Insert the new relationship - tenant_id will be set by the database trigger
    const { error } = await supabase
      .from('plotcard_tags')
      .insert([
        {
          plotcard_id: plotcardId,
          tag_id: tagId
        }
      ]);

    if (error) {
      console.error('Error adding tag to plotcard:', error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error in addTagToPlotcard:', error);
    throw error;
  }
};

/**
 * Remove a tag from a plotcard
 * @param plotcardId - Plotcard ID
 * @param tagId - Tag ID
 * @returns Promise with success status
 */
export const removeTagFromPlotcard = async (plotcardId: number, tagId: number): Promise<boolean> => {
  try {
    // Direct query to delete the relationship
    const { error } = await supabase
      .from('plotcard_tags')
      .delete()
      .eq('plotcard_id', plotcardId)
      .eq('tag_id', tagId);

    if (error) {
      console.error('Error removing tag from plotcard:', error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error in removeTagFromPlotcard:', error);
    throw error;
  }
};

/**
 * Get plotcards by tag
 * @param tagId - Tag ID
 * @returns Promise with plotcards array
 */
export const getPlotcardsByTag = async (tagId: number): Promise<any[]> => {
  try {
    // Direct query to get plotcards for a tag
    const { data, error } = await supabase
      .from('plotcard_tags')
      .select(`
        plotcard_id,
        plotcards (
          id,
          title,
          description,
          timeline_id,
          chapter_id,
          created_at,
          updated_at
        )
      `)
      .eq('tag_id', tagId)
      .order('created_at', { ascending: false, foreignTable: 'plotcards' });

    if (error) {
      console.error('Error fetching plotcards by tag:', error);
      throw error;
    }

    // Transform the data to get just the plotcards
    const transformedData = data.map(item => item.plotcards);

    return transformedData || [];
  } catch (error) {
    console.error('Error in getPlotcardsByTag:', error);
    throw error;
  }
};
